import React, { useState, useCallback, useEffect } from "react";
import { StepHeader } from "./StepHeader";
import { FullscreenModal } from "./FullscreenModal";
import { useSteps } from "../useSteps";
import { LocaleWeightInput } from "./LocaleWeightInput";
import { useLocale } from "../useLocale";
import { clsx } from "clsx";
import { OnPageRejectButton } from "./OnPageRejectButton";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./WarehouseButtonsFooter";

export const WeightCapture = ({
  title,
  instruction,
  icon,
  iconAlt,
  value,
  maxValue,
  onChange,
  showRejectButton = true,
}) => {
  const steps = useSteps();
  const { next, previous } = steps;
  const reject = steps.reject;
  const [invalidInputError, setInvalidInputError] = useState("");
  const [separatorError, setSeparatorError] = useState("");
  const locale = useLocale();

  const trackingInputCallback = useCallback((inputElement) => {
    if (inputElement) {
      inputElement?.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && value && value > 0 && !Number.isNaN(value)) {
        e.preventDefault();
        next();
      }
    },
    [value, next]
  );

  useEffect(() => {
    const invalidInput = Number.isNaN(value);
    let timeoutId;

    if (separatorError) {
      setInvalidInputError(
        separatorError === "de-DE"
          ? "Please use commas to denote decimals. For example, use 1,1 instead of 1.1."
          : separatorError === "en-US"
            ? "Please use periods to denote decimals. For example, use 1.1 instead of 1,1."
            : ""
      );
    } else if (value !== "" && (invalidInput || value <= 0)) {
      timeoutId = setTimeout(() => {
        setInvalidInputError(
          invalidInput
            ? "Please enter a valid number"
            : "Please enter a weight greater than 0"
        );
      }, 1500);
    } else {
      setInvalidInputError("");
    }

    if (value > maxValue) {
      setInvalidInputError(`Maximum value is ${maxValue}`);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, separatorError, maxValue]);

  const isValid = () => {
    return value > 0 && (!maxValue || value <= maxValue);
  };
  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <StepHeader />

        <div className="flex-1 flex flex-col items-center pt-[120px]">
          <div className="flex flex-col items-center gap-4">
            <img src={icon} alt={iconAlt} className="w-[168px] h-[168px]" />
            <div className="flex flex-col items-center gap-4">
              <h1 className="text-[29px] font-bold text-white leading-[37px] tracking-[-0.01em]">
                {title}
              </h1>
              <div className="text-[20px] font-medium text-[#C6CCE8] leading-[26px] text-center max-w-[600px]">
                {instruction}
              </div>
            </div>
          </div>
        </div>

        <WarehouseButtonsFooter error={invalidInputError}>
          <WarehouseButtonGroup>
            <WarehouseButton variant="default" onClick={() => previous()}>
              Back
            </WarehouseButton>
            {showRejectButton ? (
              <div className="w-full">
                <OnPageRejectButton />
              </div>
            ) : (
              <WarehouseButton variant="reject" onClick={() => reject()}>
                Reject
              </WarehouseButton>
            )}
            <div
              className={clsx(
                "w-full flex h-full min-w-0",
                "rounded-lg px-4 py-3.5 border focus:ring-0 text-2xl font-normal flex-1",
                {
                  "border-[#C12960]": invalidInputError,
                  "border-[#282F44]": !invalidInputError,
                }
              )}
            >
              <LocaleWeightInput
                ref={trackingInputCallback}
                className="focus:outline-none text-white bg-transparent  h-full min-w-0"
                defaultValue={value}
                locale={locale}
                onChange={onChange}
                separatorError={separatorError}
                setSeparatorError={setSeparatorError}
                onKeyDown={handleKeyDown}
              />
              <div className="flex items-center justify-center ml-auto text-white px-3 flex-shrink-0">
                kgs
              </div>
            </div>
            <WarehouseButton
              variant="primary"
              disabled={!isValid()}
              onClick={() => next()}
            >
              Continue
            </WarehouseButton>
          </WarehouseButtonGroup>
        </WarehouseButtonsFooter>
      </div>
    </FullscreenModal>
  );
};
